import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import { useI18n } from '../i18n/I18nContext';
import { resolveImageUrl } from '../utils/resolveImageUrl';
import getApiBase from '../utils/apiBase';
import { useAuth } from './AuthContext';
import './Auth.css';

const API_BASE = getApiBase();
const YANWEN_TRACKING_URL = 'https://www.yanwenexpress.com/tracking.html';

const ORDER_STATUS_KEYS = {
  pending: 'account.orderStatus.pending',
  processing: 'account.orderStatus.processing',
  paid: 'account.orderStatus.paid',
  succeeded: 'account.orderStatus.paid',
  completed: 'account.orderStatus.completed',
  cancelled: 'account.orderStatus.cancelled',
  canceled: 'account.orderStatus.cancelled',
  failed: 'account.orderStatus.failed',
  refunded: 'account.orderStatus.refunded',
};

const ACCOUNT_ROLE_KEYS = {
  customer: 'account.role.customer',
  admin: 'account.role.admin',
};

const readError = async (response, fallback) => {
  await response.json().catch(() => ({}));
  return fallback;
};

const itemImage = (item) => (
  item.previewImageUrl
  || item.imageUrl
  || item.customColorImageUrl
  || (Array.isArray(item.images) ? item.images[0] : '')
);

const Account = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const { t, formatDate, formatNumber, formatRegion } = useI18n();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');

  const sortedOrders = useMemo(() => (
    [...orders].sort((a, b) => (
      String(b.paidAt || b.createdAt || '').localeCompare(String(a.paidAt || a.createdAt || ''))
    ))
  ), [orders]);

  const fetchOrders = useCallback(async () => {
    if (!user) return;
    setOrdersLoading(true);
    setOrdersError('');
    try {
      const response = await fetch(`${API_BASE}/api/account/orders`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(await readError(response, t('account.orders.loadError')));
      }
      const data = await response.json();
      setOrders(Array.isArray(data.items) ? data.items : []);
    } catch {
      setOrdersError(t('account.orders.loadError'));
    } finally {
      setOrdersLoading(false);
    }
  }, [t, user]);

  const displayDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return formatDate(date, { dateStyle: 'medium', timeStyle: 'short' });
  };

  const shipmentLabel = (status) => {
    if (status === 'delivered') return t('account.shipment.delivered');
    if (status === 'shipped') return t('account.shipment.shipped');
    return t('account.shipment.preparing');
  };

  const orderStatusLabel = (status) => t(
    ORDER_STATUS_KEYS[String(status || '').toLowerCase()] || 'account.orderStatus.unknown'
  );
  const accountRoleLabel = (role) => t(
    ACCOUNT_ROLE_KEYS[String(role || '').toLowerCase()] || 'account.role.unknown'
  );
  const displayColor = (value) => !value || value === 'Default' ? t('common.default') : value;

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <main className="auth-page">
        <section className="auth-card">
          <h1>{t('header.account')}</h1>
          <p>{t('account.loading')}</p>
        </section>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="auth-page">
        <Seo title={t('header.account')} description={t('account.seoDescription')} path="/account" noindex />
        <section className="auth-card">
          <h1>{t('header.account')}</h1>
          <p>{t('account.signedOutPrompt')}</p>
          <div className="account-actions">
            <Link to="/login">{t('header.login')}</Link>
            <Link to="/register">{t('auth.createAccount')}</Link>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-page account-page">
      <Seo title={t('header.account')} description={t('account.seoDescription')} path="/account" noindex />
      <section className="auth-card account-profile-card">
        <h1>{t('header.account')}</h1>
        <p>{t('account.active')}</p>
        <div className="account-details">
          <div>
            <span>{t('common.name')}</span>
            <strong>{user.name || t('common.notSet')}</strong>
          </div>
          <div>
            <span>{t('common.email')}</span>
            <strong>{user.email}</strong>
          </div>
          <div>
            <span>{t('common.role')}</span>
            <strong>{accountRoleLabel(user.role)}</strong>
          </div>
        </div>
        <div className="account-actions">
          <Link to="/store">{t('account.continueShopping')}</Link>
          <Link to="/account/custom-projects">{t('account.customProjects')}</Link>
          <button type="button" onClick={handleLogout}>{t('header.logout')}</button>
        </div>
      </section>

      <section className="auth-card account-orders-card" id="orders">
        <div className="account-section-header">
          <div>
            <h2>{t('account.orders.title')}</h2>
            <p>{t('account.orders.intro')}</p>
          </div>
          <button type="button" onClick={fetchOrders} disabled={ordersLoading}>
            {ordersLoading ? t('common.loading') : t('common.refresh')}
          </button>
        </div>

        {ordersError && <div className="auth-error">{ordersError}</div>}
        {!sortedOrders.length && !ordersLoading ? <p>{t('account.orders.empty')}</p> : null}

        <div className="account-order-list">
          {sortedOrders.map((order) => {
            const trackingUrl = order.trackingUrl || YANWEN_TRACKING_URL;
            const hasTracking = Boolean(order.trackingNumber);
            return (
              <article className="account-order" key={order.id}>
                <div className="account-order-heading">
                  <div>
                    <span>{t('account.orderId')}</span>
                    <strong>{order.id}</strong>
                  </div>
                  <em>{orderStatusLabel(order.status)}</em>
                </div>
                <div className="account-order-meta">
                  <span>{t('account.paid', { date: displayDate(order.paidAt) })}</span>
                  <span>{t('account.country', { country: formatRegion(order.countryCode) })}</span>
                </div>

                <div className={`account-shipment status-${order.shipmentStatus || 'preparing'}`}>
                  <div>
                    <span>{t('account.shipment')}</span>
                    <strong>{shipmentLabel(order.shipmentStatus)}</strong>
                  </div>
                  {hasTracking ? (
                    <div>
                      <span>{t('account.trackingNumber', { carrier: order.trackingCarrier || 'Yanwen' })}</span>
                      <strong>{order.trackingNumber}</strong>
                    </div>
                  ) : (
                    <p>{t('account.trackingPending')}</p>
                  )}
                  {hasTracking ? (
                    <a href={trackingUrl} target="_blank" rel="noreferrer">
                      {t('account.trackYanwen')}
                    </a>
                  ) : null}
                </div>

                {(order.items || []).map((item) => (
                  <div className="account-order-item" key={item.itemKey || `${order.id}-${item.id}`}>
                    {itemImage(item) ? (
                      <img
                        src={resolveImageUrl(itemImage(item))}
                        alt={item.name}
                      />
                    ) : null}
                    <div>
                      <strong>{item.name}</strong>
                      <span>{displayColor(item.customColorName || item.selectedColor || item.color)}</span>
                      <span>{t('common.quantity', { quantity: item.quantity })}</span>
                      {item.price ? <span>{t('common.price', { price: formatNumber(Number(item.price), { style: 'currency', currency: 'USD' }) })}</span> : null}
                    </div>
                    {item.type === 'custom_yoyo' ? (
                      <div className="account-order-artwork-links">
                        {item.artworkUrl ? (
                          <a href={resolveImageUrl(item.artworkUrl)} target="_blank" rel="noreferrer">
                            <img src={resolveImageUrl(item.artworkUrl)} alt={t('account.originalArtworkAlt')} />
                            <span>{t('account.originalArtwork')}</span>
                          </a>
                        ) : null}
                        {item.previewImageUrl ? (
                          <a href={resolveImageUrl(item.previewImageUrl)} target="_blank" rel="noreferrer">
                            <img src={resolveImageUrl(item.previewImageUrl)} alt={t('account.adjustedPreviewAlt')} />
                            <span>{t('account.adjustedPreview')}</span>
                          </a>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ))}
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
};

export default Account;
